import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MeetingBooksLinkApiService } from '../../services/meeting-books-link-api.service';
import {
  MeetingNurtureEmailRequest,
  MeetingNurtureEmailResponse,
  MeetingEmailTemplateResponse,
} from '../../models/meeting.model';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { MeetingEmailTemplateApiService } from '../../services/meeting-email-template-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { RichTextEditorComponent } from '../../../../shared/components/rich-text-editor/rich-text-editor.component';
import { TemplateApiService } from '../../../templates/services/template-api.service';
import { AvailableVariableNamespace } from '../../../templates/models/template.model';

@Component({
  selector: 'app-booking-nurture-emails',
  standalone: true,
  imports: [CommonModule, FormsModule, RichTextEditorComponent],
  templateUrl: './booking-nurture-emails.component.html',
})
export class BookingNurtureEmailsComponent implements OnChanges {
  @Input() bookingLinkId!: number;

  /** Editores WYSIWYG en pantalla (alta + edición). Se vacían de forma síncrona antes de guardar. */
  @ViewChildren(RichTextEditorComponent) editors!: QueryList<RichTextEditorComponent>;

  items: MeetingNurtureEmailResponse[] = [];
  loading = false;
  saving = false;

  /** Plantillas de correo de reuniones para reutilizar (INC 58). */
  templates: MeetingEmailTemplateResponse[] = [];
  /** Variables merge (handlebars) disponibles para insertar en el editor. */
  variableNamespaces: AvailableVariableNamespace[] = [];

  newDelayHours: number | null = null;
  newSubject = '';
  newBodyHtml = '';
  newTemplateId: number | null = null;

  editingId: number | null = null;
  editSubject = '';
  editBodyHtml = '';
  editDelayHours: number | null = null;
  editTemplateId: number | null = null;

  constructor(
    private api: MeetingBooksLinkApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private templateApi: MeetingEmailTemplateApiService,
    private variablesApi: TemplateApiService,
    private auth: AuthService
  ) {}

  ngOnChanges(c: SimpleChanges): void {
    if (c['bookingLinkId'] && this.bookingLinkId) {
      this.load();
      this.loadTemplates();
      this.loadVariables();
    }
  }

  private loadTemplates(): void {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    this.templateApi
      .list(businessId, 0, 200)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (res) this.templates = res.content ?? [];
      });
  }

  private loadVariables(): void {
    this.variablesApi
      .getAvailableVariables()
      .pipe(catchError(() => of([] as AvailableVariableNamespace[])))
      .subscribe((ns) => (this.variableNamespaces = ns));
  }

  /** Texto plano (sin tags) para la vista previa del cuerpo en la lista. */
  bodyPreview(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return (div.textContent || '').trim();
  }

  /** Fuerza a los editores WYSIWYG a emitir su contenido antes de leerlo (evita perder el último tramo por el debounce). */
  private flushEditors(): void {
    this.editors?.forEach((e) => e.flushForSave());
  }

  /** Aplica una plantilla al formulario de alta: rellena asunto y cuerpo (INC 58). */
  applyNewTemplate(id: number | null): void {
    this.newTemplateId = id;
    if (!id) return;
    const t = this.templates.find((x) => x.id === id);
    if (!t) return;
    this.newSubject = t.subject;
    this.newBodyHtml = t.bodyHtml;
  }

  /** Aplica una plantilla al formulario de edición. */
  applyEditTemplate(id: number | null): void {
    this.editTemplateId = id;
    if (!id) return;
    const t = this.templates.find((x) => x.id === id);
    if (!t) return;
    this.editSubject = t.subject;
    this.editBodyHtml = t.bodyHtml;
  }

  load(): void {
    this.loading = true;
    this.api.listNurtureEmails(this.bookingLinkId).subscribe({
      next: (data) => {
        this.items = [...data].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.delayHours - b.delayHours
        );
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  add(): void {
    this.flushEditors();
    if (this.newDelayHours === null || !this.newSubject.trim() || !this.newBodyHtml.trim()) return;
    this.saving = true;
    const body: MeetingNurtureEmailRequest = {
      delayHours: +this.newDelayHours,
      subject: this.newSubject.trim(),
      bodyHtml: this.newBodyHtml,
      sortOrder: this.items.length,
      isActive: true,
    };
    this.api.createNurtureEmail(this.bookingLinkId, body).subscribe({
      next: () => {
        this.saving = false;
        this.newDelayHours = null;
        this.newSubject = '';
        this.newBodyHtml = '';
        this.newTemplateId = null;
        this.load();
        this.toast.created('Correo de seguimiento');
      },
      error: (err) => {
        this.saving = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear' });
      },
    });
  }

  startEdit(item: MeetingNurtureEmailResponse): void {
    this.editingId = item.id;
    this.editSubject = item.subject;
    this.editBodyHtml = item.bodyHtml;
    this.editDelayHours = item.delayHours;
    this.editTemplateId = null;
  }

  cancelEdit(): void {
    this.editingId = null;
  }

  saveEdit(item: MeetingNurtureEmailResponse): void {
    this.flushEditors();
    if (this.editDelayHours === null) return;
    this.api
      .updateNurtureEmail(this.bookingLinkId, item.id, {
        delayHours: +this.editDelayHours,
        subject: this.editSubject,
        bodyHtml: this.editBodyHtml,
        sortOrder: item.sortOrder,
        isActive: item.isActive,
      })
      .subscribe({
        next: () => {
          this.editingId = null;
          this.load();
        },
      });
  }

  toggle(item: MeetingNurtureEmailResponse): void {
    this.api
      .updateNurtureEmail(this.bookingLinkId, item.id, {
        delayHours: item.delayHours,
        subject: item.subject,
        bodyHtml: item.bodyHtml,
        sortOrder: item.sortOrder,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.load() });
  }

  async remove(item: MeetingNurtureEmailResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar email',
      message: '¿Eliminar este email?'
    });
    if (!ok) return;
    this.api.deleteNurtureEmail(this.bookingLinkId, item.id).subscribe({
      next: () => { this.load(); this.toast.deleted('Correo de seguimiento'); },
    });
  }
}
